import { AuthHelper } from "#core/helpers/auth.helper";
import { UsherPayloadHelper } from "#core/helpers/usher-payload.helper";
import { CookieService } from "#core/services";
import { TimezoneHelper } from "#helpers/timezone.helper";
import { LocalStorage } from "#services/local-storage.service";
import {inject, Inject, Injectable} from "@angular/core";
import { Router } from "@angular/router";
import { ConfigService, ServiceContextService } from "@carnegie-learning/data-access-angular";
import { SessionService } from "@carnegie-learning/data-access-angular-ct";
import { LAUNCHER_SERVICE } from "@carnegie-learning/data-access-angular-mathia";
import { Auth, FidoService, LTIService, LTI_ROLE, ProfileService, UsersService } from "@carnegie-learning/data-access-angular-passport";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { lastValueFrom, of} from "rxjs";
import { catchError, flatMap, map, switchMap, tap } from "rxjs/operators";
import * as loginFormActions from "../actions/login-form.action";
import {AuthenticationService} from "@carnegie-learning/data-access-fido";
import { AUTHENTICATION_SERVICE, ClToken2Service } from "@carnegie-learning/data-access-angular-fido";

@Injectable()
export class LoginFormEffect {
    clToken2Service = inject(ClToken2Service);
    constructor(
        private actions$: Actions,
        private auth: Auth,
        private authHelper: AuthHelper,
        private localStorage: LocalStorage,
        private usersService: UsersService,
        private profileService: ProfileService,
        private timezoneHelper: TimezoneHelper,
        private ltiService: LTIService,
        private cookieService: CookieService,
        private fidoService: FidoService,
        private configService: ConfigService,
        private serviceContext: ServiceContextService,
        private sessionService: SessionService,
        private router: Router,
        private usherPayloadHelper: UsherPayloadHelper,
        @Inject(AUTHENTICATION_SERVICE)private authenticationService: AuthenticationService
    ) {}
    // Attempts to log the user in
    /**
     * PAS-9806 revives direct login form for Demo env only
     */
    userLoginAttempt$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userLoginAttempt)).pipe(
            switchMap(action => {
                return         this.fidoService
                    .portalLogin(
                        "loginId=" +
                        encodeURIComponent(action.payload.login) +
                        "&password=" +
                        encodeURIComponent(action.payload.password)
                    ).pipe(
                        map((fidoUserData: any) =>
                            loginFormActions.userProfileAttempt()
                        ),
                        catchError(error =>
                            of(loginFormActions.userLoginFail({ payload: error }))
                        )
                );
                // return this.auth.login(action.payload).pipe(
                //     map(userData =>
                //         loginFormActions.userLoginSuccess({ payload: userData })
                //     ),
                //     catchError(error =>
                //         of(loginFormActions.userLoginFail({ payload: error }))
                //     )
                // );
            })
        )
    );

    /**
     * @deprecated
     */
    userCleverLoginAttempt$ = createEffect(() =>
        this.actions$
            .pipe(ofType(loginFormActions.userCleverLoginAttempt))
            .pipe(
                switchMap(action => {
                    return this.auth.loginWithToken(action.payload).pipe(
                        map(userData =>
                            loginFormActions.userLoginSuccess({
                                payload: userData
                            })
                        ),
                        catchError(error =>
                            of(
                                loginFormActions.userCleverLoginFail({
                                    payload: error
                                })
                            )
                        )
                    );
                })
            )
    );

    /**
     * @deprecated
     */
    userClassLinkLoginAttempt$ = createEffect(() =>
        this.actions$
            .pipe(ofType(loginFormActions.userClassLinkLoginAttempt))
            .pipe(
                switchMap(action => {
                    return this.auth.loginWithToken(action.payload).pipe(
                        map(userData =>
                            loginFormActions.userLoginSuccess({
                                payload: userData
                            })
                        ),
                        catchError(error =>
                            of(
                                loginFormActions.userClassLinkLoginFail({
                                    payload: error
                                })
                            )
                        )
                    );
                })
            )
    );

    userSampleCenterLoginAttempt$ = createEffect(() =>
        this.actions$
            .pipe(ofType(loginFormActions.userSampleCenterLoginAttempt))
            .pipe(
                switchMap(action => {
                    return this.auth.loginWithToken(action.payload).pipe(
                        map(userData => {
                            // tslint:disable-next-line:no-any
                            if (userData && (<any>userData).fidoCookies && (<any>userData).fidoCookies.length > 0) {
                                // we need to set the fido headers
                                // this is hack to get FIDO impersonation credentials from Passport api
                                // tslint:disable-next-line:no-any
                                const cookies = <string[]>(<any>userData).fidoCookies;
                                let clToken2 = "";
                                this.cookieService.deleteAll();// Need to make sure to clear cookies
                                cookies.forEach(cookie => {
                                    // Set the cookie
                                    // tslint:disable-next-line:no-any
                                    (<any>document).cookie = cookie;
                                    clToken2 = (cookie.includes("CL_TOKEN2")) ? cookie.split("=")[1] : clToken2;
                                });
                                if (clToken2) {
                                    this.clToken2Service.setClToken2(clToken2.replace(";path",""));//pick off any path info, but leave it on the cookie itself
                                }
                            }
                            return loginFormActions.userSampleCenterLoginSuccess();
                        }),
                        catchError(error =>
                            of(
                                loginFormActions.userSampleCenterLoginFail({
                                    payload: error
                                })
                            )
                        )
                    );
                })
            )
    );

    userResetPwdAttempt$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userResetPwdAttempt)).pipe(
            switchMap(action => {
                return this.usersService.resetPassword(action.payload).pipe(
                    map(userData =>
                        loginFormActions.userLoginSuccess({ payload: userData })
                    ),
                    catchError(error => of(loginFormActions.userResetPwdFail()))
                );
            })
        )
    );

    userLogout$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userLogout)).pipe(
            map(action => {
                let portalUrl: string = (<any>window).FIDO_PORTAL_RETURN_URL.toString();
                const clToken2 = this.cookieService.get("CL_TOKEN2");
                if (action.signout) {
                    this.CLSessionLogout(); // Log all the way out
                    this.usherPayloadHelper.disable();
                }
                const logoutRedirectURL = this.localStorage.get("logoutRedirectURL");
                if ((<any>window).isDemo && (logoutRedirectURL === undefined ||
                    logoutRedirectURL === null ||
                    logoutRedirectURL === "null" ||
                    logoutRedirectURL === "")) {
                    this.cookieService.deleteAll(); // only demo env requires this
                }
                if (action.signout) {
                    // PAS-13871 - edlink logout issue
                    if (clToken2) { // cookie was not deleted so need to enforce
                        this.authenticationService.logout().subscribe((response) => {
                            if (portalUrl.includes("/portal/")) {
                                (<any>window).FIDO_PORTAL_RETURN_URL = portalUrl.toLowerCase().replace("portal","login");//Fragile...
                            }
                            this.auth.logout();
                        });
                    }


                } else if (portalUrl) {
                    if(portalUrl.includes("/login/")){
                        portalUrl = portalUrl.toLowerCase().replace("login","portal");//Fragile...
                    }
                    window.location.assign(portalUrl);//navigate back to https://{{domain}}/portal/#/
                }
                return loginFormActions.noAction();
            })
        )
    );

    http503$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.http503)).pipe(
            map(action => {
                this.authHelper.logout();
                return loginFormActions.noAction();
            })
        )
    );

    http403$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.http403)).pipe(
            map(action => {
                this.authHelper.logout();
                return loginFormActions.noAction();
            })
        )
    );

    // Triggers User Profile Attempt
    /**
     * @deprecated
     */
    userLoginSuccess$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userLoginSuccess)).pipe(
            map(action => {
                const logoutRedirectURL = this.localStorage.get(
                    "logoutRedirectURL"
                );
                const requestedProgramID: number = this.localStorage.get(
                    "requestProgramID"
                );
                this.localStorage.clear();

                // Must be after the clear, url needs to persist
                if (
                    ((<any>window).isDemo ||
                        (<any>window).env === "s" ||
                        (<any>window).env === "local") &&
                    (logoutRedirectURL !== undefined &&
                        logoutRedirectURL !== null &&
                        logoutRedirectURL !== "null" &&
                        logoutRedirectURL !== "")
                ) {
                    this.localStorage.set(
                        "logoutRedirectURL",
                        logoutRedirectURL
                    );
                    this.localStorage.set(
                        "requestProgramID",
                        requestedProgramID
                    );
                }
                let date: any = this.timezoneHelper.convertServerDateToUserTZ(
                    action.payload.accessTokenExpireAt,
                    action.payload.timezone
                );
                date = date.toString();
                this.localStorage.set(
                    "authToken",
                    action.payload.accessToken,
                    date
                );
                this.localStorage.set("userProfile", action.payload);
                return loginFormActions.userProfileAttempt();
            })
        )
    );

    CLSessionLogout() {
        this.sessionService.setSession("");

        // TODO remove logging when PRPT-312 is fixed
        console.warn('logging out of CL session');

        this.configService.getConfigData().pipe(
            map(configData => configData["cl.services.ct.implementationVersion"]),
            tap(swVersion => console.warn('obtaining LauncherService for version', swVersion)),
            flatMap(swVersion => this.serviceContext.get(LAUNCHER_SERVICE, swVersion)),
            tap(_ => console.warn('calling logout on LauncherService')),
            // flatMap( launcherService => launcherService.logout())
        ).subscribe({
            next: res => console.log("CLSession logged out: ", res),
            error: err => console.error('error logging out of CL session', err)
        });

        const userProfile = this.localStorage.get('userProfile');
        if(userProfile && userProfile.roleLevel !== 1) { // roleLevel 1 as distributor shouldn't sendRequest to ltiService
            const payload = {
                url: (<any>window).AUTH_API_URI,
                return_url: (<any>window).passportHost,
                user_id: userProfile.userGUID,
                userGUID: userProfile.userGUID,
                firstName: userProfile.firstName,
                lastName: userProfile.lastName,
                email: userProfile.eAddress,
                role: userProfile.roleLevel === 5 ? LTI_ROLE.STUDENT : LTI_ROLE.TEACHER,
                destination: "home",
                school_id: userProfile.schools?.[0].schoolID,
                token: userProfile.accessToken
            };
            this.ltiService.sendRequest(payload).pipe(
                map(res => Object.entries(res.params).map(([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(String(v)))),
                // flatMap(params => this.fidoService.logout(params.join("&")))
            ).subscribe(res => console.log("CLSession logged out: ", res));
        }
    }
}
